import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react'
import { ChatClient } from 'api/clients/ChatClient'
import { useReplaceMediaBatch } from 'hooks/queries/designs/useReplaceMediaBatch'
import { useApplyCarouselChanges } from 'hooks/queries/designs/useApplyCarouselChanges'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { useMutation, useQueryClient } from 'react-query'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useRateMessage } from 'hooks/queries/chat/useRateMessage'
import { chatKeys } from 'hooks/queries/chat/chatKeys'

export type ComparisonRating = 'upvote' | 'downvote' | null

export interface ChatComparisonPayload {
  previewUrl: string
  videoUrl?: string
  designId: string
  mediaFileId: string
  mediaType: 'image' | 'video'
  slideIndex?: number
  messageId: number
  threadId: number
  rating: ComparisonRating
  generationData?: Record<string, unknown>
  action?: 'remove_slide' | 'add_slide' | 'reorder_slides'
  addedSlideIndex?: number
}

interface ChatComparisonContextValue {
  comparisons: ChatComparisonPayload[]
  setComparisons: (data: ChatComparisonPayload[]) => void
  applyOriginal: () => void
  applyModified: (payloads: ChatComparisonPayload[]) => Promise<void>
  rateComparison: (messageId: number, rating: 'upvote' | 'downvote') => void
  isApplying: boolean
  isDismissed: boolean
  isComparisonMessage: (messageId: number) => boolean
  getComparisonForMessage: (messageId: number) => ChatComparisonPayload | undefined
}

const ChatComparisonContext = createContext<ChatComparisonContextValue | null>(null)

interface TrackActionVariables {
  threadId: number
  action_type: string
  chat_message_id: number
  add_chat_message: boolean
}

export const ChatComparisonProvider = ({ children }: { children: ReactNode }) => {
  const [comparisons, setComparisonsState] = useState<ChatComparisonPayload[]>([])
  const [isApplying, setIsApplying] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const resolvedMessageIdsRef = useRef<Set<number>>(new Set())
  const workspaceId = useCurrentWorkspaceId()
  const { generationTimestampStore } = useRootStore()
  const queryClient = useQueryClient()

  const designId = comparisons[0]?.designId ?? ''
  const { mutateAsync: replaceMediaBatch } = useReplaceMediaBatch({ designId })
  const { mutateAsync: applyCarouselChanges } = useApplyCarouselChanges({ designId })
  const { mutate: rateMessage } = useRateMessage()

  const { mutate: trackAction } = useMutation<void, unknown, TrackActionVariables>(
    async (variables) => {
      await ChatClient.trackAction(workspaceId, variables.threadId, {
        action_type: variables.action_type,
        chat_message_id: variables.chat_message_id,
        add_chat_message: variables.add_chat_message,
      })
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(chatKeys.threadMessages(workspaceId, variables.threadId))
      },
    },
  )

  const setComparisons = useCallback((data: ChatComparisonPayload[]) => {
    const filtered = data.filter((c) => !resolvedMessageIdsRef.current.has(c.messageId))
    setComparisonsState((current) => {
      const currentIds = current.map((c) => c.messageId).join(',')
      const newIds = filtered.map((c) => c.messageId).join(',')
      return currentIds !== newIds ? filtered : current
    })
  }, [])

  const setFileGenerating = useCallback(
    (targetDesignId: string, isGenerating: boolean) => {
      optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, targetDesignId, (f) => ({
        ...f,
        is_generating: isGenerating,
        image_url_generating: isGenerating,
      }))
      updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, targetDesignId, (file) => ({
        ...file,
        is_generating: isGenerating,
        image_url_generating: isGenerating,
      }))
    },
    [queryClient, workspaceId],
  )

  const applyOriginal = useCallback(() => {
    const first = comparisons[0]
    comparisons.forEach((c) => resolvedMessageIdsRef.current.add(c.messageId))
    setIsDismissed(true)

    if (first && workspaceId) {
      trackAction(
        {
          threadId: first.threadId,
          action_type: 'rejected',
          chat_message_id: first.messageId,
          add_chat_message: true,
        },
        {
          onSettled: () => {
            setComparisonsState([])
            setIsDismissed(false)
          },
        },
      )
    } else {
      setComparisonsState([])
      setIsDismissed(false)
    }
  }, [comparisons, workspaceId, trackAction])

  const applyModified = useCallback(
    async (payloads: ChatComparisonPayload[]) => {
      if (payloads.length === 0) return

      // For carousel accepts, use the payload carrying the full slide snapshot
      const carouselPayload = payloads.find((p) => (p.generationData?.slides as unknown[] | undefined)?.length)
      const payload = carouselPayload ?? payloads[0]
      payloads.forEach((p) => resolvedMessageIdsRef.current.add(p.messageId))
      setIsDismissed(true)
      setIsApplying(true)
      setFileGenerating(payload.designId, true)
      generationTimestampStore.startGeneration(payload.designId, 'default')

      try {
        const slides = payload.generationData?.slides as unknown[] | undefined
        if (slides?.length) {
          await applyCarouselChanges({ generation_data: payload.generationData as Record<string, unknown> })
        } else {
          await replaceMediaBatch({
            replacements: payloads.map((p) => ({
              media_file_key: p.mediaFileId,
              ...(p.slideIndex !== undefined && { slide_index: p.slideIndex }),
              ...(p.generationData && { generation_data: p.generationData }),
            })),
          })
        }

        setComparisonsState([])
        setIsDismissed(false)

        if (workspaceId && payload.threadId) {
          trackAction({
            threadId: payload.threadId,
            action_type: 'accepted',
            chat_message_id: payload.messageId,
            add_chat_message: true,
          })
        }
      } catch {
        showToastMessage('Unable to replace', 'error')
        setFileGenerating(payload.designId, false)
        generationTimestampStore.completeGeneration(payload.designId)
        payloads.forEach((p) => resolvedMessageIdsRef.current.delete(p.messageId))
        setIsDismissed(false)
      } finally {
        setIsApplying(false)
      }
    },
    [replaceMediaBatch, applyCarouselChanges, workspaceId, generationTimestampStore, setFileGenerating, trackAction],
  )

  const rateComparison = useCallback(
    (messageId: number, rating: 'upvote' | 'downvote') => {
      const match = comparisons.find((c) => c.messageId === messageId)
      if (!match) return

      setComparisonsState((prev) => prev.map((c) => (c.messageId === messageId ? { ...c, rating } : c)))
      rateMessage({ threadId: match.threadId, messageId, rating })
    },
    [comparisons, rateMessage],
  )

  const isComparisonMessage = useCallback(
    (messageId: number) => comparisons.some((c) => c.messageId === messageId),
    [comparisons],
  )

  const getComparisonForMessage = useCallback(
    (messageId: number) => comparisons.find((c) => c.messageId === messageId),
    [comparisons],
  )

  return (
    <ChatComparisonContext.Provider
      value={{
        comparisons,
        setComparisons,
        applyOriginal,
        applyModified,
        rateComparison,
        isApplying,
        isDismissed,
        isComparisonMessage,
        getComparisonForMessage,
      }}
    >
      {children}
    </ChatComparisonContext.Provider>
  )
}

export const useChatComparison = (): ChatComparisonContextValue => {
  const context = useContext(ChatComparisonContext)
  if (!context) {
    throw new Error('useChatComparison must be used within a ChatComparisonProvider')
  }
  return context
}

export const useOptionalChatComparison = (): ChatComparisonContextValue | null => {
  return useContext(ChatComparisonContext)
}
